import { useMemo, useRef } from 'react';
import { Chart } from 'react-chartjs-2';
import { AutoSizer, Size } from 'react-virtualized';
import { INDICATOR_GROUP } from '@voladeum/client';
import { Chart as ChartJS, registerables } from 'chart.js';
import { selectSignal } from 'ducks/signal/selectors';
import { useIndicatorContext } from 'pages/context/IndicatorContext';
import { useStrategyContext } from 'pages/context/StrategyContext';
import { useAppSelector } from 'store/hooks';
import { getOscillatorAxis, getOscillatorsDataset, getTimeseriesAxis } from 'utils/charts';

import 'chartjs-adapter-moment';

import { EMPTY_ARRAY } from '../../../../../constants';

import { AdvancedGraphProps } from './types';

import cn from './IndicatorsGraph.module.scss';

ChartJS.register(...registerables);

export const OscillatorsGraph = ({ candles = EMPTY_ARRAY, visible }: AdvancedGraphProps) => {
  const oscillatorsRef = useRef<ChartJS>(null);

  const signals = useAppSelector(selectSignal);
  const { indicators: strategy_indicators } = useStrategyContext();
  const { indicators: signal_indicators } = useIndicatorContext();

  const oscillators = useMemo(() => {
    const combined_indicators = [...strategy_indicators, ...signal_indicators];
    return combined_indicators
      .filter((indicator) => indicator.indicator_group_id === INDICATOR_GROUP.OSCILLATOR)
      .flatMap((indicator) => getOscillatorsDataset(candles, signals, indicator));
  }, [candles, signal_indicators, signals, strategy_indicators]);

  const oscillator_options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: getTimeseriesAxis({ min: visible.start, max: visible.end, display: false }),
      oscillator: getOscillatorAxis(),
    },
    layout: { padding: { top: 0, right: 0, bottom: 0, left: 0 } },
    plugins: { legend: { display: false } },
  };

  return (
    <AutoSizer>
      {({ height, width }: Size) => (
        <div className={cn.graph} style={{ height, width }}>
          <Chart
            key={`oscillator-${height}-${width}`}
            ref={oscillatorsRef}
            type="line"
            data={{ labels: [], datasets: oscillators }}
            options={oscillator_options}
            width={width}
            height={height}
          />
        </div>
      )}
    </AutoSizer>
  );
};
